/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Make the mainMap match the provided location
 */

import { selectToggleValue } from '@zcode-ui/filters'
import {
  getFittedMainMapZoom,
  guessMapDimensions,
  selectMapBox,
  selectMapCenter,
  selectMapDimensions,
  selectMapUnsynced,
  selectMapVisible,
  selectMapZoom,
  show,
  sync,
  updateBox,
  updateCenter,
  updateZoom,
} from '@zcode-ui/map'
import {
  getBoxCenter,
  getFittedBoundsAndZoom,
  isValidBounds,
  isValidCenter,
  isValidDimensions,
  similarValues,
} from '@zcode/nr-utils'
import type { Coords } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import { default as getPrecisionFromZoom } from '../../../components/main/helpers/getPrecisionFromZoom'
import type { Dispatch, GetState } from '../../types'

interface Props {
  box: any
  center: {
    lat: number
    lng: number
  }
  loadNextListables?: boolean
  shouldFitBox: boolean
  zoom: number
}

export const synchronizeMapLocation =
  ({ box, center, loadNextListables, shouldFitBox = true, zoom }: Props) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const unsynced = selectMapUnsynced(state)
    const visible = selectMapVisible(state)

    if (unsynced) {
      dispatch(sync())
      return
    }

    if (!visible) {
      dispatch(show())
    }

    const toggleValue = selectToggleValue(state)
    const isFullWidthMap = toggleValue === 'full-map'
    const currentBox = selectMapBox(state)
    const currentCenter = selectMapCenter(state) as { lat: number; lng: number }
    const dimensions = guessMapDimensions(selectMapDimensions(state), isFullWidthMap) as {
      width: number
      height: number
    }
    const currentZoom = selectMapZoom(state)

    let hasValidBox = isValidBounds(box)
    let hasValidCenter = isValidCenter(center)
    const hasValidCurrentBox = currentBox && isValidBounds(currentBox)
    const hasValidDimensions = dimensions && isValidDimensions(dimensions)

    // TODO: should we validate that the center and zoom are logical for the provided box?

    // NOTE: here we both extract the center and zoom from the box as well as extract the box from the
    // center and zoom. This ensures that we end up with a box that's fit to the map size. Notice that
    // we don't extract the zoom or the box if we don't know the current map dimensions.
    if (hasValidBox && !hasValidCenter) {
      center = getBoxCenter(box) as unknown as Coords
      hasValidCenter = isValidCenter(center)
    }
    if (hasValidBox && !zoom && hasValidDimensions) {
      zoom = getFittedMainMapZoom({ box, dimensions, isFullWidthMap })
    }

    // ensure the box provided will fit in the map dimensions
    if (hasValidBox && isNumber(zoom) && hasValidDimensions && shouldFitBox) {
      ;({ box, zoom } = getFittedBoundsAndZoom({ box, center, dimensions }))
      hasValidBox = isValidBounds(box)
    }

    const precision = getPrecisionFromZoom(zoom || currentZoom)

    const shouldUpdateBox =
      hasValidBox && (!hasValidCurrentBox || !similarValues(currentBox, box, precision))
    const shouldUpdateCenter =
      (hasValidCenter || hasValidBox) && !similarValues(currentCenter, center, precision)
    const shouldClearBox = !hasValidBox && shouldUpdateCenter
    const shouldUpdateZoom = (shouldUpdateBox || shouldClearBox) && zoom !== currentZoom

    if (shouldUpdateBox) {
      dispatch(updateBox(box))
    }
    if (shouldClearBox) {
      dispatch(updateBox({}))
    }
    if (shouldUpdateCenter) {
      dispatch(updateCenter(center))
    }
    if (shouldUpdateZoom && !loadNextListables) {
      dispatch(updateZoom(zoom))
    }
  }
